import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, FILE_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Gift, Plus, Archive, ArchiveRestore, Trash2, ChevronRight, CalendarClock } from "lucide-react";
import { toast } from "sonner";

function daysUntil(iso, recurring = false) {
  if (!iso) return null;
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let target = new Date(iso + "T00:00:00");
    if (recurring) {
      // Roll month+day to this year (or next year if already past today)
      const m = target.getMonth();
      const d = target.getDate();
      const rolled = new Date(today.getFullYear(), m, d);
      if (rolled < today) rolled.setFullYear(rolled.getFullYear() + 1);
      target = rolled;
    }
    const diffMs = target.getTime() - today.getTime();
    return Math.round(diffMs / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

export default function GiftLists() {
  const { user, loading: authLoading } = useAuth();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gift-lists");
      setLists(data.lists || []);
    } catch (e) {
      toast("Couldn't load your gift lists.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user) load();
  }, [authLoading, user]);

  const createList = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    try {
      const { data: created } = await api.post("/gift-lists", { name: trimmed });
      // Prepend the new list to local state — no full reload flash.
      setLists((prev) => [{ ...created, item_count: 0 }, ...prev]);
      setName("");
      toast("List created.");
    } catch {
      toast("Couldn't create the list.");
    } finally {
      setCreating(false);
    }
  };

  const setArchived = async (id, archived) => {
    // Optimistic — the mobile "screen reload" comes from `setLoading(true)`
    // inside load(). Update state locally and skip the refetch.
    const snapshot = lists;
    setLists((prev) => prev.map((l) => (l.id === id ? { ...l, archived } : l)));
    try {
      await api.patch(`/gift-lists/${id}`, { archived });
      toast(archived ? "List archived." : "List restored.");
    } catch {
      setLists(snapshot);
      toast("Couldn't update the list.");
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and all its items? This can't be undone.`)) return;
    const snapshot = lists;
    setLists((prev) => prev.filter((l) => l.id !== id));
    try {
      await api.delete(`/gift-lists/${id}`);
      toast("List deleted.");
    } catch {
      setLists(snapshot);
      toast("Couldn't delete the list.");
    }
  };

  if (authLoading) return null;
  if (!user) {
    return (
      <main className="max-w-3xl mx-auto px-6 pt-16 pb-24 text-center">
        <p className="text-[#2A2823]/70">Sign in with Google to make a Gift List.</p>
      </main>
    );
  }

  const active = lists.filter((l) => !l.archived);
  const archived = lists.filter((l) => l.archived);

  return (
    <main className="max-w-4xl mx-auto px-6 pt-12 pb-24" data-testid="gift-lists-page">
      <div className="flex items-start gap-3">
        <Gift className="w-6 h-6 text-[#B79256] mt-3" strokeWidth={1.75} />
        <div>
          <span className="accent-label text-[#B79256]">private wishes</span>
          <h1 className="font-serif-display text-5xl text-[#2A2823] mt-2 leading-none">Gift Lists</h1>
          <p className="text-[#2A2823]/60 mt-3 max-w-lg">
            A quiet place for the things you'd love to receive. Share a list with the people you love —
            they can claim items so no one buys the same thing, and you'll never see what's been taken.
          </p>
        </div>
      </div>

      <form onSubmit={createList} className="mt-10 flex flex-col sm:flex-row gap-3">
        <Input
          data-testid="new-list-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder='Give this list a name — "My Birthday", "Christmas", "Someday"'
          className="h-11 rounded-full border-[#B79256]/40 bg-white/70 focus-visible:ring-[#4E5A3D] focus-visible:border-[#4E5A3D] px-5"
          maxLength={80}
        />
        <Button
          type="submit"
          data-testid="new-list-submit"
          disabled={creating || !name.trim()}
          className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-11 px-6 shadow-none"
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.75} />
          New list
        </Button>
      </form>

      <div className="mt-12 space-y-3" data-testid="gift-lists-active">
        {loading && <p className="text-[#2A2823]/60 text-sm">Loading your lists…</p>}
        {!loading && active.length === 0 && (
          <p className="text-[#2A2823]/60 text-sm">
            No lists yet. Give the first one a name above.
          </p>
        )}
        {active.map((l) => (
          <ListRow
            key={l.id}
            list={l}
            onArchive={() => setArchived(l.id, true)}
            onDelete={() => remove(l.id, l.name)}
          />
        ))}
      </div>

      {archived.length > 0 && (
        <div className="mt-16">
          <button
            type="button"
            data-testid="toggle-archived"
            onClick={() => setShowArchived((v) => !v)}
            className="accent-label text-[#2A2823]/60 hover:text-[#2A2823] transition-colors"
          >
            {showArchived ? "Hide archived" : `Show archived (${archived.length})`}
          </button>
          {showArchived && (
            <div className="mt-4 space-y-3 opacity-80" data-testid="gift-lists-archived">
              {archived.map((l) => (
                <ListRow
                  key={l.id}
                  list={l}
                  archived
                  onRestore={() => setArchived(l.id, false)}
                  onDelete={() => remove(l.id, l.name)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </main>
  );
}

function ListRow({ list, archived, onArchive, onRestore, onDelete }) {
  const cover = list.cover_photo_path ? FILE_URL(list.cover_photo_path) : null;
  return (
    <div
      data-testid={`list-row-${list.id}`}
      className="group relative overflow-hidden rounded-3xl border border-[#B79256]/25 bg-white/60 hover:bg-white/80 transition-colors"
    >
      <div className="flex items-stretch gap-0">
        <Link
          to={`/gift-lists/${list.id}`}
          className="shrink-0 relative w-20 sm:w-40 min-h-[120px] bg-[#B79256]/10"
          aria-label={`Open ${list.name}`}
        >
          {cover ? (
            <img
              src={cover}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-[#B79256]/40">
              <Gift className="w-8 h-8" strokeWidth={1.25} />
            </div>
          )}
          <div className="absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white/80 to-transparent" />
        </Link>
        <Link
          to={`/gift-lists/${list.id}`}
          className="flex-1 min-w-0 flex flex-col justify-center px-3 sm:px-5 py-4"
        >
          <div className="font-serif text-xl sm:text-2xl text-[#2A2823] truncate">{list.name}</div>
          <div className="text-xs text-[#2A2823]/50 mt-1 flex items-center gap-2 flex-wrap">
            <span>
              {list.item_count === 1 ? "1 item" : `${list.item_count} items`}
            </span>
            {list.share_token && (
              <span className="accent-label text-[#4E5A3D]">sharing on</span>
            )}
            {(() => {
              const d = daysUntil(list.occasion_date, list.occasion_recurring);
              if (d === null) return null;
              if (d < 0) return null;
              if (d <= 30) {
                const label = d === 0 ? "today" : d === 1 ? "in 1 day" : `in ${d} days`;
                const urgent = d <= 7;
                return (
                  <span
                    data-testid={`occasion-nudge-${list.id}`}
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 border ${
                      urgent
                        ? "border-[#B79256] bg-[#B79256]/15 text-[#7A5A2E]"
                        : "border-[#4E5A3D]/30 bg-[#4E5A3D]/8 text-[#4E5A3D]"
                    }`}
                  >
                    <CalendarClock className="w-3 h-3" strokeWidth={2} />
                    <span className="accent-label" style={{ color: "inherit" }}>{label} — top it up?</span>
                    {list.occasion_recurring && (
                      <span className="opacity-70" title="Recurs every year">·↻</span>
                    )}
                  </span>
                );
              }
              return (
                <span className="inline-flex items-center gap-1 text-[#2A2823]/50">
                  <CalendarClock className="w-3 h-3" strokeWidth={1.75} />
                  {new Date(list.occasion_date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  {list.occasion_recurring && (
                    <span className="opacity-70" title="Recurs every year">·↻</span>
                  )}
                </span>
              );
            })()}
          </div>
        </Link>
        <div className="flex items-center pr-1 sm:pr-3">
          {archived ? (
            <button
              type="button"
              onClick={onRestore}
              data-testid={`restore-list-${list.id}`}
              aria-label="Restore list"
              className="p-1.5 sm:p-2 rounded-full text-[#2A2823]/50 hover:text-[#4E5A3D] hover:bg-[#4E5A3D]/10 transition-colors"
            >
              <ArchiveRestore className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={1.75} />
            </button>
          ) : (
            <button
              type="button"
              onClick={onArchive}
              data-testid={`archive-list-${list.id}`}
              aria-label="Archive list"
              className="p-1.5 sm:p-2 rounded-full text-[#2A2823]/50 hover:text-[#2A2823] hover:bg-[#B79256]/15 transition-colors"
            >
              <Archive className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={1.75} />
            </button>
          )}
          <button
            type="button"
            onClick={onDelete}
            data-testid={`delete-list-${list.id}`}
            aria-label="Delete list"
            className="p-1.5 sm:p-2 rounded-full text-[#2A2823]/50 hover:text-red-800 hover:bg-red-800/10 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={1.75} />
          </button>
          <Link
            to={`/gift-lists/${list.id}`}
            aria-label="Open list"
            className="p-1.5 sm:p-2 rounded-full text-[#2A2823]/40 hover:text-[#2A2823] transition-colors"
          >
            <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={1.75} />
          </Link>
        </div>
      </div>
    </div>
  );
}
